'use client';

// Editor + previsualizador GENÉRICO de un documento de decisión (Indagación previa /
// Auto de apertura). Parametrizado por un DocDecisionConfig (plantilla + campos +
// prellenado). Misma convención que AutoInhibitorioDialog: previsualiza el .docx real
// (con logos/cabeceras/pie) vía docx-preview, permite editar los campos, descargar .docx
// y "Descargar PDF" (render → imprimir → Guardar como PDF). Por ahora NO persiste borrador.

import { useEffect, useRef, useState } from 'react';
import { Download, Eye, FileText, Loader2, Printer, Save } from 'lucide-react';
import { toast } from 'sonner';
import type { ExpedienteDetalle } from '@/lib/expedientes';
import {
  blobDoc, descargarDoc, type DocDecisionConfig, type DatosDoc,
} from '@/lib/plantillas/decisiones-docs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

export default function DocumentoDecisionDialog({
  exp, cfg, onClose, soloLectura = false, guardado, onGuardar,
}: {
  exp: ExpedienteDetalle;
  cfg: DocDecisionConfig;
  onClose: () => void;
  soloLectura?: boolean;
  guardado?: DatosDoc | null; // borrador ya persistido de este documento
  onGuardar?: (datos: DatosDoc) => Promise<void> | void; // persistir el borrador
}) {
  // Arranca del borrador guardado (si existe) sobre los datos autogenerados del expediente.
  const [datos, setDatos] = useState<DatosDoc>(() => ({ ...cfg.prefill(exp), ...(guardado ?? {}) }));
  const [render, setRender] = useState(false);
  const [expPdf, setExpPdf] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);
  const yaRenderizo = useRef(false);

  const set = (k: string, v: string) => setDatos((d) => ({ ...d, [k]: v }));

  async function guardarBorrador() {
    if (!onGuardar) return;
    setGuardando(true);
    try {
      await onGuardar(datos);
      toast.success('Borrador guardado. El revisor verá este texto.');
    } catch (e) {
      toast.error('No se pudo guardar el borrador', {
        description: e instanceof Error ? e.message : undefined,
      });
    } finally {
      setGuardando(false);
    }
  }

  async function previsualizar() {
    setRender(true);
    try {
      const blob = await blobDoc(cfg, datos);
      const { renderAsync } = await import('docx-preview');
      // Leemos el ref DESPUÉS del await: en el doble-montaje de StrictMode el div se
      // recrea, así renderizamos en el contenedor vivo (no en uno ya desmontado).
      const host = previewRef.current;
      if (!host) return;
      host.innerHTML = '';
      await renderAsync(blob, host, undefined, {
        className: 'docx', inWrapper: true, ignoreLastRenderedPageBreak: true,
      });
    } catch (e) {
      toast.error('No se pudo generar la previsualización', {
        description: e instanceof Error ? e.message : undefined,
      });
    } finally {
      setRender(false);
    }
  }

  // Previsualización automática al abrir (una sola vez). Se dispara directo al montar
  // —sin setTimeout ni cleanup que la cancele— para no quedar en blanco por StrictMode.
  useEffect(() => {
    if (yaRenderizo.current) return;
    yaRenderizo.current = true;
    void previsualizar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function descargarDocx() {
    try {
      await descargarDoc(cfg, datos.RADICADO, datos);
      toast.success('Documento descargado (.docx).');
    } catch (e) {
      toast.error('No se pudo descargar el documento', {
        description: e instanceof Error ? e.message : undefined,
      });
    }
  }

  async function descargarPDF() {
    setExpPdf(true);
    try {
      const blob = await blobDoc(cfg, datos);
      const { renderAsync } = await import('docx-preview');
      const w = window.open('', '_blank', 'width=920,height=1000');
      if (!w) { toast.error('Permita las ventanas emergentes para exportar a PDF.'); return; }
      w.document.title = `${cfg.plantillaId.toLowerCase()}-${datos.RADICADO || 'documento'}`;
      // Evita que el cuerpo tape el logo de la cabecera (ver globals.css).
      const st = w.document.createElement('style');
      st.textContent = '.docx-wrapper section.docx > header, .docx-wrapper section.docx > footer { z-index: 2; }';
      w.document.head.appendChild(st);
      await renderAsync(blob, w.document.body, undefined, { className: 'docx', inWrapper: true });
      toast.message('En el diálogo de impresión elija "Guardar como PDF".');
      setTimeout(() => { w.focus(); w.print(); }, 500);
    } catch (e) {
      toast.error('No se pudo exportar a PDF', {
        description: e instanceof Error ? e.message : undefined,
      });
    } finally {
      setExpPdf(false);
    }
  }

  const panelPreview = (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium text-muted-foreground">Previsualización</span>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={previsualizar} disabled={render}>
            {render ? <Loader2 className="size-3.5 animate-spin" /> : <Eye className="size-3.5" />}
            Actualizar
          </Button>
          {!soloLectura && onGuardar && (
            <Button size="sm" variant="outline" onClick={guardarBorrador} disabled={guardando}>
              {guardando ? <Loader2 className="size-3.5 animate-spin" /> : <Save className="size-3.5" />}
              Guardar borrador
            </Button>
          )}
          {!soloLectura && (
            <Button size="sm" variant="outline" onClick={descargarDocx}>
              <Download className="size-3.5" /> .docx
            </Button>
          )}
          <Button size="sm" onClick={descargarPDF} disabled={expPdf}>
            {expPdf ? <Loader2 className="size-3.5 animate-spin" /> : <Printer className="size-3.5" />}
            Descargar PDF
          </Button>
        </div>
      </div>
      <div className="relative max-h-[74vh] overflow-auto rounded-md border bg-neutral-100 text-black">
        {render && (
          <div className="pointer-events-none absolute inset-x-0 top-0 z-10 p-4 text-center text-sm text-gray-500">
            Generando…
          </div>
        )}
        <div ref={previewRef} className="p-2" />
      </div>
    </div>
  );

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="w-[80vw] max-w-[80vw] sm:max-w-[80vw]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="size-5 text-primary" /> {cfg.titulo}
          </DialogTitle>
          <DialogDescription>
            {soloLectura
              ? 'Previsualización del documento con los datos del expediente.'
              : 'Los campos vienen prellenados con los datos del expediente. Edítelos, previsualice y descargue. (El mapeo detallado del cuerpo se ajustará luego.)'}
          </DialogDescription>
        </DialogHeader>

        {soloLectura ? (
          panelPreview
        ) : (
          <div className="grid gap-6 md:grid-cols-[340px_1fr]">
            <div className="max-h-[74vh] space-y-3 overflow-y-auto pr-2">
              {cfg.campos.map((c) => (
                <div key={c.key} className="space-y-1.5">
                  <Label htmlFor={`f-${c.key}`}>{c.label}</Label>
                  {c.multiline ? (
                    <Textarea id={`f-${c.key}`} rows={4} value={datos[c.key] ?? ''}
                      onChange={(e) => set(c.key, e.target.value)} />
                  ) : (
                    <Input id={`f-${c.key}`} value={datos[c.key] ?? ''}
                      onChange={(e) => set(c.key, e.target.value)} />
                  )}
                </div>
              ))}
            </div>
            {panelPreview}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
